import { RepeatIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Checkbox,
  Heading,
  HStack,
  IconButton,
  Spinner,
  Text,
  VStack
} from "@chakra-ui/react";
import type { SyncHistory } from "@shared/models";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import PaginationWrapper from "~/components/PaginationWrapper";
import { formatShortDateTime } from "~/helpers/date.helper";
import Meta from "~/layout/Meta";
import { fetchArray } from "~/services/fetch-array";

const SyncHistoryPage = () => {
  const [includeVerbose, setIncludeVerbose] = useState(false);
  const [hideOnlySkipped, setHideOnlySkipped] = useState(true);
  const [hideSuccessfulNoChanges, setHideSuccessfulNoChanges] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const recentSyncHistory = useQuery({
    queryKey: ["sync-history", includeVerbose, hideOnlySkipped, hideSuccessfulNoChanges],
    queryFn: () => fetchArray<SyncHistory>(`/api/orders/sync-history?verbose=${includeVerbose}`, "sync history")
  });

  const lastSyncRun = recentSyncHistory.data?.[0];

  const filteredSyncHistory = useMemo(() => {
    return recentSyncHistory.data?.filter((x) => {
      if (
        hideOnlySkipped &&
        x.numOrdersSkipped > 0 &&
        x.numOrdersSynced === 0 &&
        x.numOrdersErrored === 0 &&
        x.status === "success"
      ) {
        return false;
      }

      if (
        hideSuccessfulNoChanges &&
        x.status === "success" &&
        x.numOrdersSynced === 0 &&
        x.numOrdersErrored === 0 &&
        x.numOrdersSkipped === 0
      ) {
        return false;
      }

      return true;
    });
  }, [recentSyncHistory.data, hideOnlySkipped, hideSuccessfulNoChanges]);

  // Calculate pagination
  const totalPages = Math.ceil((filteredSyncHistory?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSyncHistory = filteredSyncHistory?.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, []);

  if (recentSyncHistory.isFetching) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" pt={10}>
        <Spinner />
      </Box>
    );
  }

  const buildNameValueLine = (name: string, value: string | number) => {
    return (
      <Text>
        <Text fontWeight={600} as="span">
          {name}:{" "}
        </Text>
        {value}
      </Text>
    );
  };

  return (
    <>
      <Meta title="Sync History" />

      <VStack gap="4" alignItems="flex-start" mb="4">
        <HStack>
          <Heading as="h1">Sync History (past 7 days)</Heading>
          <IconButton aria-label="Refresh Orders" icon={<RepeatIcon />} onClick={() => recentSyncHistory.refetch()} />
        </HStack>
        {lastSyncRun && (
          <Badge colorScheme={lastSyncRun.status === "success" ? "green" : "red"}>
            Last run: {formatShortDateTime(new Date(lastSyncRun.createdOn))}
          </Badge>
        )}
        <HStack>
          <Checkbox isChecked={hideOnlySkipped} onChange={(e) => setHideOnlySkipped(e.target.checked)}>
            Only show "Synced" or "Errored"
          </Checkbox>
          <Checkbox isChecked={hideSuccessfulNoChanges} onChange={(e) => setHideSuccessfulNoChanges(e.target.checked)}>
            Hide successful syncs with no changes
          </Checkbox>
          <Checkbox isChecked={includeVerbose} onChange={(e) => setIncludeVerbose(e.target.checked)}>
            Include verbose logs
          </Checkbox>
        </HStack>
        {recentSyncHistory.isError && (
          <Alert status="error">
            <AlertIcon />
            {recentSyncHistory.error.message}
          </Alert>
        )}
      </VStack>

      <VStack gap="10" w={"100%"}>
        {currentSyncHistory?.map((x) => (
          <VStack key={x.id} bg="white" p="4" align="start" gap="1" alignContent={"start"} borderRadius="lg" w={"100%"}>
            <HStack alignItems="flex-start" gap="10" mb="4">
              <VStack align="start" gap="0">
                <HStack>
                  {buildNameValueLine("Sync Run ID", x.id.toString())}
                  <Badge colorScheme={x.status === "success" ? "green" : "red"}>{x.status}</Badge>
                </HStack>
                {buildNameValueLine("Sync Duration", x.durationSec)}
                {buildNameValueLine("Synced On", formatShortDateTime(new Date(x.createdOn)))}
                {buildNameValueLine("Num orders", x.numOrders)}
              </VStack>
              <VStack align="start" gap="0">
                {buildNameValueLine("Synced", x.numOrdersSynced)}
                {buildNameValueLine("Skipped", x.numOrdersSkipped)}
                {buildNameValueLine("Errored", x.numOrdersErrored)}
              </VStack>
            </HStack>
            <Text fontWeight={600}>Logs:</Text>
            <Box whiteSpace="pre-wrap" overflow="hidden" textOverflow="ellipsis" maxW="100%" wordBreak="break-word">
              {x.logs}
            </Box>
          </VStack>
        ))}
      </VStack>
      {!recentSyncHistory.isError && filteredSyncHistory?.length === 0 && <Box>No results</Box>}
      <PaginationWrapper
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        startIndex={startIndex}
        endIndex={endIndex}
        totalItems={filteredSyncHistory?.length || 0}
        itemType="sync runs"
      />
    </>
  );
};

export default SyncHistoryPage;
